import { useState, useCallback } from 'react'
import { channelsAPI } from '../services/api'
import { toast } from '../services/toastService'
import { trackChannelRateLimitHit, trackMessageSent, trackActivation } from '../utils/analytics'
import { createLogger } from '../utils/logger'
import useOnboardingStepComplete from './useOnboardingStepComplete'

const logger = createLogger('ChannelMessageSend')

/**
 * useChannelMessageSend - Handles sending messages in channels
 * 
 * @atomic - Single responsibility: message sending
 * @idempotent - Same inputs produce same outputs
 * @modular - Self-contained message sending logic
 * 
 * @param {boolean} isAuthenticated - Whether user is authenticated
 * @param {Object} activeChannel - Current active channel
 * @param {boolean} canPost - Whether user can post (rate limit check)
 * @param {Function} checkRateLimit - Function to check rate limit
 * @param {Function} fetchChannels - Function to refresh channel list
 * @param {Function} t - Translation function
 * @returns {Object} Message sending state and handlers
 */
export function useChannelMessageSend(isAuthenticated, activeChannel, canPost, checkRateLimit, fetchChannels, t) {
  const [newMessage, setNewMessage] = useState('')
  const [sending, setSending] = useState(false)
  const completeOnboardingStep = useOnboardingStepComplete()

  const handleSendMessage = useCallback(async (e) => {
    e.preventDefault()
    
    // For non-authenticated users, show toast prompting them to sign in
    if (!isAuthenticated) {
      toast.info(t('channels.contactForm.signInRequired'), {
        onClick: () => {
          // The parent component should handle showing auth forms
          // This just provides feedback to the user
        }
      })
      return
    }

    if (!newMessage.trim() || !activeChannel || !canPost) return

    // Validate minimum length
    if (newMessage.trim().length < 10) {
      toast.error(t('channels.message.minLength'))
      return
    }

    setSending(true)
    try {
      const response = await channelsAPI.postMessage(activeChannel.id, newMessage.trim())
      // Don't add message here - let WebSocket broadcast handle it to avoid duplicates
      setNewMessage('')
      completeOnboardingStep('messaged')
      trackMessageSent(activeChannel.id, newMessage.trim().length)
      trackActivation('first_message', { channel_id: activeChannel.id })
      
      // Refresh channel list to update unread counts for other channels
      // This ensures notifications are up-to-date after posting
      fetchChannels(true) // Silent mode to avoid loading spinner
      
      // Update rate limit from response if available
      if (response.data.rateLimit) {
        await checkRateLimit()
      }
    } catch (error) {
      logger.error('Failed to send message', error, { channelId: activeChannel?.id })
      
      // Handle rate limit errors with specific messages
      if (error.response?.status === 429) {
        const errorData = error.response.data
        const reason = errorData.reason
        const minutesRemaining = errorData.retryAfter ? Math.ceil(errorData.retryAfter / 60) : 0
        
        // Track rate limit hit for analytics
        trackChannelRateLimitHit(activeChannel.id, reason || 'unknown', minutesRemaining)
        
        let errorMsg = t('channels.message.rateLimitError')
        if (reason === 'minimum_interval') {
          errorMsg = t('channels.message.rateLimitWait')
        } else if (reason === 'burst_limit') {
          errorMsg = t('channels.message.rateLimitBurst', { count: errorData.burstLimit })
        } else if (reason === 'hourly_limit') {
          errorMsg = t('channels.message.rateLimitHourly', { count: errorData.hourlyLimit })
        } else if (errorData.message) {
          errorMsg = errorData.message
        }
        
        toast.error(errorMsg)
        await checkRateLimit()
      } else {
        const errorMsg = error.response?.data?.error || t('channels.message.error')
        toast.error(errorMsg)
      }
    } finally {
      setSending(false)
    }
  }, [isAuthenticated, newMessage, activeChannel, canPost, checkRateLimit, fetchChannels, t])

  return {
    newMessage,
    setNewMessage,
    sending,
    handleSendMessage
  }
}
